/**
 * ============================================================================
 * gameProgressionService.ts - Game State Monitor
 * ============================================================================
 *  ENTERPRISE: SIMPLIFIED - Server now handles ALL progression

 * 
 * This service now ONLY:
 * - Monitors game state for UI updates
 * - Triggers next hand after delay (owner only)
 * 
 * Server handles:
 * - All-in runouts (automatic)
 * - Showdown resolution
 * - Phase advancement
 * - Winner determination
 * ============================================================================
 */

import { onSnapshot, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import gameApiService from './gameApiService';
import { GameState } from '../types/GameState';

// ============================================================================
// GAME PROGRESSION SERVICE - ENTERPRISE (MONITOR ONLY)
// ============================================================================

class GameProgressionService {
  // Track active monitors by table ID
  private activeMonitors: Map<string, () => void> = new Map();
  
  // Track processing state
  private processingTables: Set<string> = new Set();
  
  // Next hand timers
  private nextHandTimers: Map<string, NodeJS.Timeout> = new Map();

  // ============================================================================
  // MONITOR MANAGEMENT
  // ============================================================================

  /**
   * Start monitoring a table for game state changes
   *  SIMPLIFIED: Only monitors, doesn't drive progression

   */
  startMonitoring(tableId: string): void {
    // Prevent duplicate monitors
    if (this.activeMonitors.has(tableId)) {
      console.log(`⚠️ [GameProgression] Already monitoring table ${tableId}`);
      return;
    }

    console.log(`🚀 [GameProgression] Starting monitor for table ${tableId}`);

    // Set up Firebase real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'tables', tableId),
      (snapshot) => {
        if (!snapshot.exists()) {
          console.log(`❌ [GameProgression] Table ${tableId} no longer exists`);
          this.stopMonitoring(tableId);
          return;
        }

        const tableData = snapshot.data();
        const gameState = tableData?.gameState as GameState | undefined;

        if (!gameState) {
          return;
        }

        // 🏆 Tournament tables are dealt EXCLUSIVELY by the server's
        // tournament scheduler (blind levels, breaks, eliminations,
        // consolidation all live there). Driving /startHand from the client
        // dealt cash-style hands straight through breaks and the blind
        // schedule.
        if (tableData.isTournament || tableData.tournamentId || (gameState as any).tournamentId) {
          return;
        }

        //  Only one player handles next hand scheduling to avoid duplicates

        // For owner-created tables: the owner handles it
        // For featured/system tables (no ownerId): first player in array handles it
        const currentUserId = auth.currentUser?.uid;
        const realPlayers = (tableData.players || []).filter((p: string) => !p.startsWith('house_bot_'));
        const effectiveOwner = tableData.ownerId || realPlayers[0];
        if (currentUserId !== effectiveOwner) {
          return;
        }

        // Check if hand just ended and we need to schedule next hand
        if (gameState.status === 'ended' || gameState.round === 'ended') {
          this.maybeScheduleNextHand(tableId, tableData);
        }
      },
      (error) => {
        console.error(`❌ [GameProgression] Listener error for ${tableId}:`, error);
        this.stopMonitoring(tableId);
      }
    );

    this.activeMonitors.set(tableId, unsubscribe);
  }

  /**
   * Stop monitoring a specific table
   */
  stopMonitoring(tableId: string): void {
    const unsubscribe = this.activeMonitors.get(tableId);
    if (unsubscribe) {
      unsubscribe();
      this.activeMonitors.delete(tableId);
      this.processingTables.delete(tableId);

      // Clear any pending timers
      const timer = this.nextHandTimers.get(tableId);
      if (timer) {
        clearTimeout(timer);
        this.nextHandTimers.delete(tableId);
      }

      console.log(`🛑 [GameProgression] Stopped monitoring table ${tableId}`);
    }
  }

  /**
   * Stop all active monitors (cleanup on app shutdown)
   */
  stopAll(): void {
    console.log(`🛑 [GameProgression] Stopping all monitors`);
    
    this.activeMonitors.forEach((unsubscribe, tableId) => {
      unsubscribe();
      console.log(`   - Stopped ${tableId}`);
    });

    // Clear all timers
    this.nextHandTimers.forEach(timer => clearTimeout(timer));

    this.activeMonitors.clear();
    this.processingTables.clear();
    this.nextHandTimers.clear();
  }

  // ============================================================================
  // NEXT HAND SCHEDULING (Owner Only)
  // ============================================================================

  /**
   * Schedule next hand if not already scheduled
   */
  private maybeScheduleNextHand(tableId: string, tableData: any): void {
    // Don't schedule if already scheduled
    if (this.nextHandTimers.has(tableId)) {
      return;
    }

    // Check if enough players for next hand
    const players = tableData.players || [];
    if (players.length < 2) {
      console.log(`⚠️ [GameProgression] Not enough players for next hand`);
      return;
    }

    const gameState = tableData.gameState;
    
    //  FIX: Check if this was an all-in showdown

    // If so, DON'T schedule - let PokerTable's auto-start handle it (waits for runout animation)
    // UNLESS only 1 player is left (someone left mid-showdown)
    if (gameState && Array.isArray(gameState.players)) {
      const nonFoldedPlayers = gameState.players.filter((p: any) => p && !p.folded);
      
      //  CRITICAL FIX: Use hand-level flags, NEVER player.allIn (gets cleared by backend!)

      // - allInOccurred: set immediately when any player goes all-in
      // - allInShowdown: set when runout/showdown begins
      const hasAllInFlag = gameState.allInOccurred === true || gameState.allInShowdown === true;
      const isFoldWin = gameState.handResult?.allOthersFolded === true || nonFoldedPlayers.length <= 1;
      
      //  FIX: Check if enough players have chips (not busted)

      const playersWithChips = gameState.players.filter((p: any) =>
        p && p.chips > 0 && !p.leavingTable
      );

      //  FIX: If only 1 player with chips, don't schedule next hand

      // BUT if there's a bot at the table, don't reset - the bot will be re-funded by the server
      if (playersWithChips.length < 2) {
        const hasBotAtTable = (tableData.players || []).some((p: string) => p.startsWith('house_bot_'));
        if (hasBotAtTable) {
          console.log(`⏳ [GameProgression] Not enough players with chips: ${playersWithChips.length}, but bot present - waiting for server to re-fund bot`);
          // Don't schedule and don't reset - server will handle bot rebuy
          return;
        }
        console.log(`⚠️ [GameProgression] Not enough players with chips: ${playersWithChips.length}`);
        // Don't schedule - let PokerTable's auto-start handle the reset to lobby
        return;
      }
      
      //  Only let PokerTable handle if there are 2+ players still active for runout

      // Use hasAllInFlag instead of checking individual player.allIn flags
      if (hasAllInFlag && !isFoldWin && nonFoldedPlayers.length >= 2) {
        console.log(`⏳ [GameProgression] All-in showdown detected - letting PokerTable handle timing (runout animation)`);
        return;
      }
    }

    // Schedule next hand (fold wins only - no runout animation needed)
    const DELAY_FOR_RESULTS = 8000; // 8 seconds to show results
    
    console.log(`📅 [GameProgression] Scheduling next hand in ${DELAY_FOR_RESULTS / 1000}s (fold win)`);

    const timer = setTimeout(() => {
      this.nextHandTimers.delete(tableId);
      this.startNewHand(tableId);
    }, DELAY_FOR_RESULTS);

    this.nextHandTimers.set(tableId, timer);
  }

  /**
   * Start a new hand via server API
   */
  private async startNewHand(tableId: string): Promise<void> {
    // Prevent concurrent attempts
    if (this.processingTables.has(tableId)) {
      return;
    }

    try {
      this.processingTables.add(tableId);
      console.log(`🔄 [GameProgression] Starting new hand for ${tableId}`);

      const result = await gameApiService.startHand(tableId);
      
      if (result.success) {
        console.log(`✅ [GameProgression] New hand started`);
      } else {
        //  FIX: Log the reason for failure

        const reason = (result as any).reason || 'unknown';
        console.log(`⏸️ [GameProgression] Could not start new hand: ${reason}`);
      }

    } catch (error) {
      console.error(`❌ [GameProgression] Error starting new hand:`, error);
    } finally {
      this.processingTables.delete(tableId);
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Check if a table is being monitored
   */
  isMonitoring(tableId: string): boolean {
    return this.activeMonitors.has(tableId);
  }

  /**
   * Get the number of actively monitored tables
   */
  getActiveMonitorCount(): number {
    return this.activeMonitors.size;
  }

  /**
   * Force start next hand (admin/debug use)
   */
  async forceStartHand(tableId: string): Promise<void> {
    console.log(`⚡ [GameProgression] Force starting hand for ${tableId}`);
    await this.startNewHand(tableId);
  }

  /**
   *  DEPRECATED: Server handles showdown now

   * Kept for backward compatibility but does nothing
   */
  async checkAndProgressToShowdown(tableId: string, gameState: GameState): Promise<boolean> {
    console.warn('⚠️ checkAndProgressToShowdown is deprecated - server handles this');
    return false;
  }

  /**
   *  DEPRECATED: Server handles phase advancement now

   * Kept for backward compatibility but does nothing
   */
  async checkAndProgressRound(tableId: string, gameState: GameState): Promise<void> {
    console.warn('⚠️ checkAndProgressRound is deprecated - server handles this');
  }

  /**
   *  DEPRECATED: Server handles walkovers now

   */
  async handleWalkover(tableId: string, winnerId: string): Promise<void> {
    console.warn('⚠️ handleWalkover is deprecated - server handles this');
  }
}

// ============================================================================
// EXPORT
// ============================================================================

const gameProgressionService = new GameProgressionService();
export default gameProgressionService;